import type { ReplayMetricKey, ReplayPlayerMetrics } from "@/entities/replay-metrics";

/**
 * KAN-876: место игрока внутри роли по одной оси (эпик KAN-804).
 *
 * ПОЧЕМУ РАНГ СЧИТАЕТСЯ, А НЕ ВЫВОДИТСЯ ИЗ ПЕРЦЕНТИЛЯ. Соблазн такой: перцентиль и
 * `peerCount` известны, значит место равно `peerCount * (1 - percentile)`. Дело не в
 * округлении — преобразование необратимо ПРИНЦИПИАЛЬНО. Бэкенд считает перцентиль как
 * «доля строго ниже плюс половина равных» (`PercentileMath.Rank`), то есть mid-rank:
 * при трёх одинаковых значениях все трое получают ОДИН перцентиль, и того, какое из
 * трёх мест чьё, в нём просто не записано. Достать оттуда «5-й из 8» значит вернуть
 * число, которого в данных нет: выглядит как наблюдение, наблюдением не является.
 *
 * Считать честно можно, потому что у витрины на руках ВСЯ выборка: ответ
 * `/tournaments/{id}/replay-metrics` содержит всех игроков турнира. Место — это
 * пересчёт по тем же данным, а не восстановление по агрегату.
 *
 * СВЕРКА С `peerCount` ОБЯЗАТЕЛЬНА. Группу сравнения собирал бэкенд, и его правила
 * отбора (порог игр KAN-725, нормализация роли) могут не совпасть с тем, что видно
 * отсюда. Если размер собранной группы разошёлся с `peerCount`, место не показывается
 * вовсе: лучше промолчать, чем назвать человека пятым из восьми, когда сравнивали его
 * с другой восьмёркой.
 */

export interface AxisRank {
  /** Место внутри роли, 1 — лучший. */
  rank: number;
  /** Сколько всего в группе сравнения. */
  peerCount: number;
}

export function computeAxisRank(
  players: ReplayPlayerMetrics[],
  player: ReplayPlayerMetrics,
  key: ReplayMetricKey,
): AxisRank | null {
  const own = player.metrics.find((m) => m.key === key);
  if (!own || own.value === null || own.percentile === null) return null;

  const role = own.peerGroupRole;
  if (!role) return null;

  const peers: number[] = [];
  for (const candidate of players) {
    const metric = candidate.metrics.find((m) => m.key === key);
    // В группу входят только те, кого бэкенд сравнивал: с посчитанным перцентилем и
    // той же группой роли. Остальные в его выборку не попали, и добавлять их сюда
    // значило бы считать место по другой совокупности.
    if (!metric || metric.value === null || metric.percentile === null) continue;
    if (metric.peerGroupRole !== role) continue;
    peers.push(metric.value);
  }

  // Расхождение с бэкендом — молчим. См. пояснение выше.
  if (peers.length !== own.peerCount) return null;
  if (peers.length === 0) return null;

  const better = peers.filter((value) => value > own.value!).length;

  return { rank: better + 1, peerCount: peers.length };
}
